import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function PrivacyPage() {
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const sections = document.querySelectorAll("section[data-section]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.4 }
    );

    sections.forEach((sec) => observer.observe(sec));
    return () => observer.disconnect();
  }, []);

  const tocItems = [
    { id: "intro", label: "1. Introduction" },
    { id: "who", label: "2. Who We Are" },
    { id: "collect", label: "3. Information We Collect" },
    { id: "use", label: "4. How We Use Information" },
    { id: "legal", label: "5. Legal Basis" },
    { id: "sharing", label: "6. Sharing Your Info" },
    { id: "retention", label: "7. Data Retention" },
    { id: "children", label: "8. Children & Teens" },
    { id: "security", label: "9. Data Security" },
    { id: "transfers", label: "10. International Transfers" },
    { id: "rights", label: "11. Your Rights" },
    { id: "updates", label: "12. Policy Changes" },
    { id: "contact-global", label: "13. Contact (Omnyx)" },

    { id: "ig-header", label: "InGroove – Product Privacy" },
    { id: "ig-users", label: "1. User Data" },
    { id: "ig-hubs", label: "2. Hub Data" },
    { id: "ig-payment", label: "3. Groovies & Payments" },
    { id: "ig-chats", label: "4. Event Chats" },
    { id: "ig-operators", label: "5. Hub Operators" },
    { id: "ig-media", label: "6. Media Uploads" },
    { id: "ig-payouts", label: "7. Payouts" },
    { id: "ig-liability", label: "8. Fraud & Liability" },
    { id: "ig-datashare", label: "9. Data Sharing" },
    { id: "ig-dataretention", label: "10. Retention" },
    { id: "contact", label: "Contact (InGroove)" },
  ];

  return (
    <div className="relative mx-auto flex max-w-site gap-10 px-6 py-20">
      {/* TABLE OF CONTENTS */}
      <aside className="hidden h-fit w-64 lg:sticky lg:top-24 lg:block">
        <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-wide text-site-ink">On this page</h3>
        <ul className="space-y-2 border-l border-site-hairline pl-4">
          {tocItems.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className={`block text-sm transition-colors ${
                  activeSection === item.id
                    ? "font-semibold text-omnyx-teal"
                    : "text-site-muted hover:text-site-ink"
                }`}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="mt-6 border-t border-site-hairline pt-4">
          <Link
            to="/terms"
            className="font-semibold text-omnyx-teal hover:text-omnyx-teal-dark"
          >
            → Terms & Conditions
          </Link>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <div className="legal-content flex-1 space-y-10">
        <div className="border-b border-site-hairline pb-10">
          <h1 className="font-heading text-4xl font-extrabold tracking-tightest text-site-ink md:text-5xl">
            Omnyx – Privacy Policy
          </h1>
          <p className="mt-3 text-site-muted">
            Last Updated: {new Date().getFullYear()}
          </p>
        </div>

        {/* INTRO */}
        <section id="intro" data-section className="mb-10 scroll-mt-28">
          <p>
            Omnyx (Pty) Ltd (“Omnyx”, “we”, “us”, “our”) is committed to
            protecting your privacy. This Privacy Policy explains how we
            collect, use, store, disclose, and protect your personal information
            when you use any Omnyx-owned platforms, apps, websites, or services.
          </p>
          <p className="mt-4">
            This policy applies globally and includes product-specific
            extensions for platforms built under Omnyx, including InGroove.
          </p>
        </section>

        {/* WHO WE ARE */}
        <section id="who" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">2. Who We Are</h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>Omnyx (Pty) Ltd – South African technology company</li>
            <li>Owner/Director: Nkosinathi Mabuza</li>
            <li>Email: legal@omnyx.co.za</li>
            <li>Parent company of: InGroove</li>
            <li>
              All data collected on any Omnyx product may be accessible to Omnyx
              as the parent entity.
            </li>
          </ul>
        </section>

        {/* INFORMATION WE COLLECT */}
        <section id="collect" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">
            3. Information We Collect
          </h2>

          <h3 className="font-semibold mt-4 mb-1">3.1 Personal Information</h3>
          <ul className="list-disc ml-6 space-y-2">
            <li>Full Name</li>
            <li>Email Address</li>
            <li>Phone Number</li>
            <li>Country of Residence</li>
            <li>Profile Photo (optional)</li>
            <li>Password (encrypted)</li>
          </ul>

          <h3 className="font-semibold mt-4 mb-1">
            3.2 Usage & Technical Information
          </h3>
          <ul className="list-disc ml-6 space-y-2">
            <li>Device info, IP address, browser type</li>
            <li>App/site interactions & log data</li>
            <li>Cookies (where applicable)</li>
            <li>Analytics usage data</li>
          </ul>

          <h3 className="font-semibold mt-4 mb-1">
            3.3 Content Provided By You
          </h3>
          <ul className="list-disc ml-6 space-y-2">
            <li>Bios, descriptions</li>
            <li>Uploaded media, banners</li>
            <li>Business info (creators/organizers)</li>
          </ul>

          <h3 className="font-semibold mt-4 mb-1">
            3.4 Payment-Related Information
          </h3>
          <p>
            Payments are processed through PayFast or Ozow. Omnyx does not store
            card or authentication details.
          </p>
        </section>

        {/* USE */}
        <section id="use" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">
            4. How We Use Your Information
          </h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>Operate and maintain Omnyx Services</li>
            <li>Create and secure accounts</li>
            <li>Improve performance & user experience</li>
            <li>Fraud prevention & safety monitoring</li>
            <li>Legal compliance</li>
          </ul>
          <p className="mt-2">We do NOT sell your personal data.</p>
        </section>

        {/* LEGAL BASIS */}
        <section id="legal" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">
            5. Legal Basis for Processing
          </h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>Your consent</li>
            <li>Contractual necessity</li>
            <li>Legal obligations</li>
            <li>Legitimate interest</li>
          </ul>
        </section>

        {/* SHARING */}
        <section id="sharing" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">
            6. Sharing Information
          </h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>Service providers (hosting, analytics, payments)</li>
            <li>Regulatory authorities where required</li>
            <li>Other Omnyx products for integrated services</li>
          </ul>
          <p className="mt-2">
            We do NOT share personal data with advertisers.
          </p>
        </section>

        {/* RETENTION */}
        <section id="retention" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">7. Data Retention</h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>While your account is active</li>
            <li>As required to operate services</li>
            <li>For legal or audit requirements</li>
          </ul>
          <p>You may request account deletion anytime.</p>
        </section>

        {/* CHILDREN */}
        <section id="children" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">8. Children & Teens</h2>
          <p>Some Omnyx products may allow users under 18.</p>
          <ul className="list-disc ml-6 space-y-2 mt-2">
            <li>Minimal data collection</li>
            <li>Parental deletion rights</li>
            <li>Under 13 may require consent depending on region</li>
          </ul>
        </section>

        {/* SECURITY */}
        <section id="security" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">9. Data Security</h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>Encrypted passwords</li>
            <li>HTTPS/TLS transmission</li>
            <li>Internal access controls</li>
            <li>Security monitoring</li>
          </ul>
        </section>

        {/* TRANSFERS */}
        <section id="transfers" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">
            10. International Data Transfers
          </h2>
          <p>
            Data may be transferred globally as Omnyx expands, with appropriate
            protections applied.
          </p>
        </section>

        {/* RIGHTS */}
        <section id="rights" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">11. Your Rights</h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>Access your data</li>
            <li>Correct inaccurate info</li>
            <li>Request deletion</li>
            <li>Withdraw consent</li>
            <li>Object to processing</li>
            <li>Request portability</li>
          </ul>
        </section>

        {/* UPDATES */}
        <section id="updates" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">
            12. Changes to This Policy
          </h2>
          <p>
            We may update this Policy periodically. Continued use of Omnyx
            Services means you accept the updated terms.
          </p>
        </section>

        {/* CONTACT GLOBAL */}
        <section
          id="contact-global"
          data-section
          className="mb-20 scroll-mt-28"
        >
          <h2 className="text-2xl font-semibold mb-3">
            13. Contact Us (Omnyx)
          </h2>
          <p>Email: legal@omnyx.co.za</p>
        </section>

        {/* INGROOVE HEADER */}
        <section
          id="ig-header"
          data-section
          className="mt-20 mb-10 scroll-mt-28"
        >
          <h2 className="mb-6 font-heading text-3xl font-extrabold tracking-tightest text-omnyx-teal">
            InGroove – Product-Specific Privacy Policy
          </h2>
          <p className="opacity-90">
            This section extends the Omnyx Global Privacy Policy with
            platform-specific details for InGroove.
          </p>
        </section>

        {/* IG USERS */}
        <section id="ig-users" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">
            1. Personal Data Collected for Users
          </h3>
          <p>For ticket buyers, we collect:</p>
          <ul className="list-disc ml-6 space-y-2 mt-2">
            <li>Name & Surname</li>
            <li>Email Address</li>
            <li>Phone Number</li>
            <li>Country</li>
            <li>Password</li>
            <li>Profile Picture (optional)</li>
          </ul>
        </section>

        {/* IG HUBS */}
        <section id="ig-hubs" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">
            2. Additional Data for Hubs (Organizers)
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li>Hub Name</li>
            <li>Physical Address</li>
            <li>Hub Bio</li>
            <li>Banner Image</li>
            <li>Uploaded Media</li>
            <li>Bank Account Name</li>
            <li>Bank Account Number</li>
          </ul>

          <p className="mt-3">
            Banking information is encrypted and used ONLY for payouts.
          </p>
        </section>

        {/* IG PAYMENT */}
        <section id="ig-payment" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">
            3. Payments & Groovies Tokens
          </h3>

          <p>
            Users purchase Groovies using PayFast or Ozow. Groovies are used to
            buy tickets. We do NOT store card details.
          </p>

          <p className="mt-3 font-semibold">Refund Policy (Privacy Note)</p>
          <ul className="list-disc ml-6 space-y-2 mt-1">
            <li>Refunds for cancelled events are issued in Groovies only</li>
            <li>No cash refunds except where required by South African law</li>
            <li>Groovies cannot be exchanged for cash</li>
          </ul>
        </section>

        {/* IG CHATS */}
        <section id="ig-chats" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">4. Event Chats</h3>
          <p>We store chat messages for:</p>
          <ul className="list-disc ml-6 space-y-2 mt-2">
            <li>Platform functionality</li>
            <li>Safety and abuse detection</li>
            <li>Legal compliance</li>
          </ul>
          <p className="mt-3">
            InGroove does NOT actively monitor chats unless required for safety
            or legal reasons.
          </p>
        </section>

        {/* IG OPERATORS */}
        <section id="ig-operators" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">5. Hub Operators</h3>
          <p>Hub Admins may add Operators to assist with event management.</p>
          <ul className="list-disc ml-6 space-y-2 mt-2">
            <li>Admins are responsible for operators they add</li>
            <li>Operators receive limited access only</li>
            <li>Omnyx/InGroove is not liable for misuse by Operators</li>
          </ul>
        </section>

        {/* IG MEDIA */}
        <section id="ig-media" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">6. Media Uploads</h3>
          <ul className="list-disc ml-6 space-y-2">
            <li>Only Hubs may upload promotional media</li>
            <li>Stored securely</li>
            <li>Removable upon request</li>
            <li>Must follow content requirements</li>
          </ul>
        </section>

        {/* IG PAYOUTS */}
        <section id="ig-payouts" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">7. Payout Processing</h3>
          <p>After events end:</p>
          <ul className="list-disc ml-6 space-y-2 mt-2">
            <li>Hubs request payout from Hub Wallet</li>
            <li>Verification occurs</li>
            <li>Funds paid to bank via gateway or manual transfer</li>
            <li>Payout logs kept for audits</li>
          </ul>
        </section>

        {/* IG LIABILITY */}
        <section id="ig-liability" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">
            8. Fraud, Misconduct & Liability
          </h3>
          <p>InGroove is a platform only — a point of sale.</p>

          <ul className="list-disc ml-6 space-y-2 mt-2">
            <li>Not liable for event performance or quality</li>
            <li>Not liable for Hub fraud or misconduct</li>
            <li>Not liable for injuries, damages, or disputes</li>
            <li>Not liable for misuse by Hub operators</li>
          </ul>

          <p className="mt-2">
            Hubs may be suspended for fraud or unsafe activity.
          </p>
        </section>

        {/* IG DATA SHARING */}
        <section id="ig-datashare" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">
            9. Data Sharing Within InGroove
          </h3>
          <ul className="list-disc ml-6 space-y-2">
            <li>Limited data shared with Hubs (ticket buyers list)</li>
            <li>Payment gateways (transactions only)</li>
            <li>Authorities when legally required</li>
            <li>Analytics (technical usage only)</li>
          </ul>

          <p className="mt-2">Never shared with advertisers.</p>
        </section>

        {/* IG RETENTION */}
        <section
          id="ig-dataretention"
          data-section
          className="mb-10 scroll-mt-28"
        >
          <h3 className="text-xl font-semibold mb-3">
            10. Data Retention (InGroove)
          </h3>
          <ul className="list-disc ml-6 space-y-2">
            <li>Chats: kept for as long as hub/event exists</li>
            <li>Purchases: kept for legal obligations</li>
            <li>Payouts: retained for audits</li>
            <li>Media: until removed by hub</li>
            <li>Account data: until user deletes account</li>
          </ul>
        </section>

        {/* CONTACT */}
        <section id="contact" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">Contact (InGroove)</h2>
          <p>Email: legal@omnyx.co.za</p>

          <Link
            to="/terms"
            className="inline-block mt-6 font-semibold text-omnyx-teal hover:text-omnyx-teal-dark"
          >
            → Terms & Conditions
          </Link>
        </section>
      </div>
    </div>
  );
}
